import React, { useState, useRef, useEffect } from 'react';

const YouTubeCarousel = ({
  videos = [],
  className = '',
  showDots = true,
  showArrows = true,
  accentColor = '#749555',
  layout = 'linkblock'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const carouselRef = useRef(null);

  const goToSlide = (index) => {
    if (isTransitioning || videos.length === 0) return;

    setIsTransitioning(true);
    setCurrentIndex(index);

    setTimeout(() => setIsTransitioning(false), layout === 'tabs' ? 250 : 300);
  };

  const nextSlide = () => {
    // Circular navigation for all layouts
    const nextIndex = (currentIndex + 1) % videos.length;
    goToSlide(nextIndex);
  };

  const prevSlide = () => {
    // Circular navigation for all layouts
    const prevIndex = currentIndex === 0 ? videos.length - 1 : currentIndex - 1;
    goToSlide(prevIndex);
  };

  // Touch/Mouse drag handlers
  const handleStart = (clientX) => {
    if (videos.length <= 1) return;
    setIsDragging(true);
    setStartX(clientX);
    setDragOffset(0);
  };

  const handleMove = (clientX) => {
    if (!isDragging || videos.length <= 1) return;

    const deltaX = clientX - startX;
    setDragOffset(deltaX);
  };

  const handleEnd = () => {
    if (!isDragging || videos.length <= 1) return;

    setIsDragging(false);

    // Determine if we should switch slides based on drag distance
    const threshold = 50;
    const carouselWidth = carouselRef.current?.offsetWidth || 300;
    const dragPercentage = Math.abs(dragOffset) / carouselWidth;

    if (layout === 'tabs') {
      // Tab layout: simple threshold
      if (Math.abs(dragOffset) > threshold) {
        if (dragOffset > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
      }
    } else {
      // Link block layout: threshold + percentage check
      if (Math.abs(dragOffset) > threshold && dragPercentage > 0.2) {
        if (dragOffset > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
      }
    }

    setDragOffset(0);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (videos.length <= 1) return;

      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, videos.length]);

  // Add global mouse move/up listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, startX]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!videos || videos.length === 0) {
    const bgColor = layout === 'tabs' ? 'bg-gray-800' : 'bg-gray-100';
    const textColor = layout === 'tabs' ? 'text-gray-400' : 'text-gray-500';
    return (
      <div className={`${bgColor} rounded-lg flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <i className="fab fa-youtube text-4xl text-red-500 mb-4"></i>
          <p className={textColor}>No videos to display</p>
        </div>
      </div>
    );
  }

  // Tab Layout: Modern Peek Carousel
  if (layout === 'tabs') {
    const hasPrev = videos.length >= 3; // Left peek (prev) - show only if 3+ items
    const hasNext = videos.length >= 2; // Right peek (next) - show if 2+ items

    // Mobile: Single centered video only
    if (isMobile) {
      return (
        <div className={`relative w-full ${className}`} ref={carouselRef}>
          <div className="flex justify-center">
            <div
              className="w-full max-w-lg"
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={`relative w-full rounded-xl overflow-hidden shadow-2xl transition-opacity duration-250 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`} style={{ aspectRatio: '16 / 9' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${videos[currentIndex].videoId}?modestbranding=1&rel=0&showinfo=0`}
                  title={videos[currentIndex].title || `Video ${currentIndex + 1}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
                ></iframe>
              </div>

              {/* Video Info */}
              {(videos[currentIndex].title || videos[currentIndex].description) && (
                <div className="mt-4 text-center">
                  {videos[currentIndex].title && (
                    <h3 className="text-xl font-bold text-white mb-1">{videos[currentIndex].title}</h3>
                  )}
                  {videos[currentIndex].description && (
                    <p className="text-gray-400 text-sm">{videos[currentIndex].description}</p>
                  )}
                </div>
              )}

              {/* Counter */}
              {videos.length > 1 && (
                <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                  <i
                    className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                    onClick={prevSlide}
                  ></i>
                  <span>{currentIndex + 1} / {videos.length}</span>
                  <i
                    className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                    onClick={nextSlide}
                  ></i>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Desktop: Peek preview layout with wrap-around
    const nextIndex = (currentIndex + 1) % videos.length;
    const prevIndex = (currentIndex - 1 + videos.length) % videos.length;

    return (
      <div className={`relative w-full overflow-visible ${className}`} ref={carouselRef}>
        {/* Modern Peek Carousel */}
        <div className="relative flex items-center justify-center gap-4">
          {/* Previous Peek */}
          {hasPrev && (
            <div
              className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
              onClick={isTransitioning ? undefined : prevSlide}
            >
              <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75" style={{ aspectRatio: '16 / 9' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${videos[prevIndex].videoId}?modestbranding=1&rel=0&showinfo=0&controls=0&disablekb=1`}
                  title={videos[prevIndex].title || `Video ${prevIndex + 1}`}
                  frameBorder="0"
                  className="w-full h-full pointer-events-none"
                ></iframe>
                <div className="absolute inset-0 bg-black/40"></div>
              </div>
            </div>
          )}

          {/* Main Video */}
          <div
            className="flex-1"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className={`relative w-full rounded-xl overflow-hidden shadow-2xl transition-opacity duration-250 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`} style={{ aspectRatio: '16 / 9' }}>
              <iframe
                src={`https://www.youtube.com/embed/${videos[currentIndex].videoId}?modestbranding=1&rel=0&showinfo=0`}
                title={videos[currentIndex].title || `Video ${currentIndex + 1}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
              ></iframe>
            </div>

            {/* Video Info */}
            {(videos[currentIndex].title || videos[currentIndex].description) && (
              <div className="mt-4 text-center">
                {videos[currentIndex].title && (
                  <h3 className="text-xl font-bold text-white mb-1">{videos[currentIndex].title}</h3>
                )}
                {videos[currentIndex].description && (
                  <p className="text-gray-400 text-sm">{videos[currentIndex].description}</p>
                )}
              </div>
            )}

            {/* Counter */}
            {videos.length > 1 && (
              <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                <i
                  className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={prevSlide}
                ></i>
                <span>{currentIndex + 1} / {videos.length}</span>
                <i
                  className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={nextSlide}
                ></i>
              </div>
            )}
          </div>

          {/* Next Peek */}
          {hasNext && (
            <div
              className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
              onClick={isTransitioning ? undefined : nextSlide}
            >
              <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75" style={{ aspectRatio: '16 / 9' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${videos[nextIndex].videoId}?modestbranding=1&rel=0&showinfo=0&controls=0&disablekb=1`}
                  title={videos[nextIndex].title || `Video ${nextIndex + 1}`}
                  frameBorder="0"
                  className="w-full h-full pointer-events-none"
                ></iframe>
                <div className="absolute inset-0 bg-black/40"></div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Navigation Arrows */}
        <div className="hidden">
          <button
            onClick={prevSlide}
            disabled={!hasPrev}
            className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center"
            aria-label="Previous video"
          >
            <i className="fas fa-chevron-left text-white"></i>
          </button>
          <button
            onClick={nextSlide}
            disabled={!hasNext}
            className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center"
            aria-label="Next video"
          >
            <i className="fas fa-chevron-right text-white"></i>
          </button>
        </div>
      </div>
    );
  }

  // Link Block Layout: Original TV Frame Design
  const transform = `translateX(${-currentIndex * 100 + (dragOffset / (carouselRef.current?.offsetWidth || 300)) * 100}%)`;

  return (
    <div className={`relative ${className}`} ref={carouselRef}>
      {/* Video Counter - Top right of whole container */}
      {videos.length > 1 && (
        <div className="absolute top-2 right-2 bg-chess-cream text-gray-800 px-3 py-1.5 text-sm font-bold shadow-lg border-2 border-black z-10">
          {currentIndex + 1} / {videos.length}
        </div>
      )}

      {/* TV Container - Full width with parent padding */}
      <div className="relative w-full mx-auto max-w-2xl">
        {/* TV Frame - Retro TV style with wood/dark cabinet */}
        <div
          className="p-6 rounded-lg shadow-2xl border-4"
          style={{
            background: `linear-gradient(135deg, #4a3728 0%, #2d1f15 100%)`,
            borderColor: '#4a3728',
            boxShadow: '0 15px 50px rgba(0, 0, 0, 0.6), inset 0 2px 4px rgba(255, 255, 255, 0.1)'
          }}
        >
          {/* TV Screen Bezel - Inner chrome/metallic border */}
          <div
            className="p-2 rounded-2xl"
            style={{
              background: `linear-gradient(to bottom, ${accentColor} 0%, #1a1a1a 100%)`,
              boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* TV Screen - Black background for video */}
            <div className="relative w-full bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
              <div
                className={`flex h-full transition-transform ${isTransitioning && !isDragging ? 'duration-300 ease-out' : 'duration-0'}`}
                style={{ transform }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {videos.map((video, index) => {
                  // Extract video ID from URL or use direct videoId
                  let videoId = video.videoId;
                  if (!videoId && video.url) {
                    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
                    const match = video.url.match(youtubeRegex);
                    videoId = match ? match[1] : null;
                  }

                  return (
                    <div key={video.id || index} className="flex-none w-full h-full bg-black">
                      {videoId ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={video.title || `Video ${index + 1}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                          style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
                        ></iframe>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <p className="text-white">Invalid video</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Dots Indicator */}
              {showDots && videos.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2">
                  {videos.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        index === currentIndex
                          ? 'bg-gray-200 scale-125'
                          : 'bg-gray-500 hover:bg-gray-400'
                      }`}
                      disabled={isTransitioning}
                      aria-label={`Go to video ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* TV Control Panel Area with Navigation Arrows */}
          <div className="mt-4 min-h-[60px] flex items-center justify-between">
            {/* Left Arrow */}
            {showArrows && videos.length > 1 && (
              <button
                onClick={prevSlide}
                className="bg-chess-cream hover:bg-chess-cream/80 text-black rounded-full w-12 h-12 flex items-center justify-center transition-all shadow-md hover:scale-110 flex-shrink-0"
                disabled={isTransitioning}
                aria-label="Previous video"
              >
                <i className="fas fa-chevron-left text-xl"></i>
              </button>
            )}

            {/* Video Title - Center */}
            <div className="flex-1 px-4">
              {videos[currentIndex] && (videos[currentIndex].title || videos[currentIndex].description) ? (
                <div className="text-center space-y-1">
                  {videos[currentIndex].title && (
                    <h3 className="font-bold text-xl text-gray-100">{videos[currentIndex].title}</h3>
                  )}
                  {videos[currentIndex].description && (
                    <p className="text-gray-300 text-sm">{videos[currentIndex].description}</p>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-400 text-sm italic">Video {currentIndex + 1}</p>
                </div>
              )}
            </div>

            {/* Right Arrow */}
            {showArrows && videos.length > 1 && (
              <button
                onClick={nextSlide}
                className="bg-chess-cream hover:bg-chess-cream/80 text-black rounded-full w-12 h-12 flex items-center justify-center transition-all shadow-md hover:scale-110 flex-shrink-0"
                disabled={isTransitioning}
                aria-label="Next video"
              >
                <i className="fas fa-chevron-right text-xl"></i>
              </button>
            )}
          </div>
        </div>

        {/* Drag indicator */}
        {isDragging && (
          <div className="absolute inset-0 bg-black/10 pointer-events-none rounded-3xl" />
        )}
      </div>
    </div>
  );
};

export default YouTubeCarousel;
